// apps/merchant-v3/src/lib/auth/RequireAuth.tsx
//
// Phase 6 Wave 1 Task 03 — Route guard wrapper. Renders the children
// only when `useAuth()` reports an authenticated user. Behaviour:
//
//   - bootstrap loading → render <LoadingShell />.
//   - unauthenticated   → <Navigate to="/login?returnUrl=<current-path>"
//                         replace /> (deep-link preservation).
//   - suspended user    → render the SuspendOverlay on disallowed
//                         routes. Allowlist:
//                         /settings/security/*, /support, /403, /404,
//                         /500. Read-only banners (Task 42) continue
//                         to render at the top of every authenticated
//                         page so the lockdown signal is unmissable.
//
// The single-flight 401 refresh in `lib/api/client.ts` keeps this guard
// idempotent — page chunks may make API calls after we've rendered;
// when those 401, the api/client triggers refresh and (on failure)
// dispatches the unauthorized-handler installed by AuthProvider, which
// in turn navigates back to /login. RequireAuth is the "first line" —
// it prevents naked navigation BEFORE any data fetch happens.
//
// Phase 6 Wave 12 Task 43 — wires SuspendOverlay into the
// guard. When `useUser().merchantSuspended === true` AND the active
// route is NOT on the allowlist, RequireAuth swaps the children for
// the read-only overlay surface. The overlay lives ABOVE the children
// (z-50) so even a stale unmount of the previous page leaves no
// interactive surface beneath the lockdown.
//
// vg-binding: PLAN:task-03
// vg-binding: PLAN:task-43
// vg-binding: P6.D-02
// vg-binding: P6.D-114
// vg-binding: P6.D-205
// vg-binding: P6.D-248

import { type JSX, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { LoadingShell } from '@/components/LoadingShell';
import {
  SuspendOverlay,
  isSuspendAllowedPath,
} from '@/components/security/SuspendOverlay';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useUser } from '@/store/user';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { user: authUser, isLoading, isUnauthenticated } = useAuth();
  const portalUser = useUser();
  const location = useLocation();

  if (isLoading) {
    return <LoadingShell />;
  }

  if (isUnauthenticated || !authUser) {
    // Preserve deep link — `returnUrl` points back to the path the user
    // attempted. Skip the encoding when target is "/" or
    // already on /login to keep the URL clean.
    const path = location.pathname + location.search;
    const target =
      path && path !== '/' && !path.startsWith('/login')
        ? '/login?returnUrl=' + encodeURIComponent(path)
        : '/login';
    return <Navigate to={target} replace />;
  }

  // suspended merchants only see allowlisted
  // routes; everything else swaps to the SuspendOverlay surface. We
  // STILL render the children behind the overlay so React Router state
  // (scroll, modals) remains intact for the moment Admin un-suspends.
  if (
    portalUser?.merchantSuspended &&
    !isSuspendAllowedPath(location.pathname)
  ) {
    return (
      <>
        {children}
        <SuspendOverlay />
      </>
    );
  }

  return <>{children}</>;
}

export default RequireAuth;
